import React, { useState, useEffect, useRef } from "react";
import { FaGamepad } from "react-icons/fa";

interface LazyImageProps {
    src?: string;
    alt?: string;
    className?: string;
    fallbackIcon?: React.ReactNode;
    onError?: () => void;
}

const LazyImage: React.FC<LazyImageProps> = ({
    src,
    alt = "",
    className = "",
    fallbackIcon = <FaGamepad />,
    onError,
}) => {
    const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
    const [imageError, setImageError] = useState(false);
    const imgRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!src || !imgRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setImageSrc(src);
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: "50px", // Start loading 50px before image enters viewport
            },
        );

        observer.observe(imgRef.current);

        return () => {
            observer.disconnect();
        };
    }, [src]);

    const handleError = () => {
        setImageError(true);
        onError?.();
    };

    if (!src || imageError) {
        return (
            <div className={`${className} lazy-image__fallback`} ref={imgRef}>
                {fallbackIcon}
            </div>
        );
    }

    return (
        <div ref={imgRef} className={`${className}-wrapper`}>
            {imageSrc
                ? <img
                    src={imageSrc}
                    alt={alt}
                    className={className}
                    onError={handleError}
                />
                : <div className={`${className} lazy-image__placeholder`}>
                    <div className="lazy-image__shimmer" />
                </div>
            }
        </div>
    );
};

export default LazyImage;